import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { env } from '../env';
import { getLocalStorage } from '../utils/function/localStorage';

export const chatApi = createApi({
  reducerPath: 'chatApi',

  baseQuery: fetchBaseQuery({
    baseUrl: env.BASE_URL,
    prepareHeaders: (headers) => {
      const token = getLocalStorage('tp_Aa_uth_access');
      if (token) {
        headers.set('X-Auth-Token', token);
      }
      return headers;
    },
  }),

  // Merged tags from both App and Business to ensure proper cache invalidation
  tagTypes: [
    'chatWithUs',
    'adminChatGroups',
    'adminGroupMessages',
    'userChat',
    'Chat',
    'MessageNotifications',
    'ChatRewardClaims',
    'AutomatedCategories',
    'AutomatedTags',
    'AutomatedMessages'
  ],
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: false,

  endpoints: (builder) => ({
    // ==========================================
    // NEW ENDPOINTS IMPORTED FROM APP API
    // ==========================================
    getAllConversationUsers: builder.query({
      query: () => ({
        url: '/chat/fetch_conversation_by_users',
        method: 'GET',
      }),
      providesTags: ['Chat'],
    }),

    getConversationUsers: builder.query({
      query: (body) => ({
        url: `/chat/fetch_conversation_users?username=${body.username}`,
        method: 'GET',
      }),
      providesTags: ['Chat'],
    }),

    getAllMessages: builder.query({
      query: (body) => ({
        url: `/chat/get_message?conversationId=${body.conversationId}`,
        method: 'GET',
      }),
      providesTags: ['Chat'],
    }),

    getSingleTask: builder.query({
      query: (body) => ({
        url: `/secure/fetch_single_task?id=${body.task_id}`,
        method: 'GET',
      }),
      providesTags: ['Chat'],
    }),

    getGroupMessages: builder.query({
      query: (task_id) => ({
        url: `/chat/get_group_message?task_id=${task_id}`,
        method: 'GET',
      }),
      providesTags: ['Chat'],
    }),

    addGroupMessage: builder.mutation({
      query: (body) => ({
        url: '/chat/add_group_message',
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['Chat'],
    }),

    getAvailableGroups: builder.query({
      query: () => ({
        url: '/chat/get_availaible_groups',
        method: 'GET',
      }),
      providesTags: ['Chat'],
    }),

    deleteMessageByAdmin: builder.mutation({
      query: (body) => ({
        url: '/chat/deleteMessageByAdmin',
        method: 'PUT',
        body: { id: body.id },
      }),
      invalidatesTags: ['Chat'],
    }),

    addCreatorChatMessage: builder.mutation({
      query: (body) => ({
        url: '/chat/addCreatorChatMessage',
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['Chat'],
    }),

    getCreatorChatMessage: builder.query({
      query: ({ taskId, chatWithUser }) => ({
        url: `/chat/getCreatorChatMessage?task_id=${taskId}&chatWithUser=${chatWithUser}`,
        method: 'GET',
      }),
      providesTags: ['Chat'],
    }),

    // --- Message Notifications ---
    getMessageNotifications: builder.query({
      query: ({ page = 1, limit = 20 } = {}) =>
        `/chat/message-notifications?page=${page}&limit=${limit}`,
      providesTags: ['MessageNotifications'],
    }),
    markMessageNotificationSeen: builder.mutation({
      query: (notificationId) => ({
        url: `/chat/message-notifications/${notificationId}/seen`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MessageNotifications'],
    }),
    deleteManyMessageNotifications: builder.mutation({
      query: ({ group_id }) => ({
        url: `/chat/message-notifications/${group_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MessageNotifications'],
    }),
    deleteAllMessageNotifications: builder.mutation({
      query: () => ({
        url: `/chat/delete-all-message-notifications`,
        method: 'DELETE',
      }),
    }),

    // ==========================================
    // EXISTING BUSINESS ADMIN CHAT ENDPOINTS
    // ==========================================
    getGroupIdwithAdmin: builder.query({
      query: () => ({
        url: '/chat/getGroupIdwithAdmin',
        method: 'GET',
      }),
    }),
    getGroupIdwithAdminV2: builder.query({
      query: () => ({
        url: '/chat/getGroupIdwithAdminV2',
        method: 'GET',
      }),
    }),
    getGroupIdwithAdminV3: builder.query({
      query: () => ({
        url: '/chat/getGroupIdwithAdminV3',
        method: 'GET',
      }),
    }),

    getAdminGroupMessagesV2: builder.query({
      query: ({ groupId, limit = 30, cursor }) => ({
        url: `/chat/getAdminGroupMessagesV2`,
        method: 'GET',
        params: {
          groupId: groupId,
          limit,
          ...(cursor ? { cursor } : {}),
        },
      }),
      providesTags: ['adminGroupMessages'],
    }),

    addAdminChatMessage: builder.mutation({
      query: (body) => ({
        url: '/chat/addAdminChatMessage',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['adminGroupMessages'],
    }),

    addAdminChatImage: builder.mutation({
      query: (formData) => ({
        url: '/chat/addAdminChatImage',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['adminGroupMessages'],
    }),

    getAdminChatGroups: builder.query({
      query: () => ({
        url: '/chat/getAdminChatGroups',
        method: 'GET',
      }),
      providesTags: ['adminChatGroups'],
      keepUnusedDataFor: 300,
    }),

    getAdminGroupMessages: builder.query({
      query: (groupId) => ({
        url: `/chat/getAdminGroupMessages/${groupId}`,
        method: 'GET',
      }),
      providesTags: ['adminGroupMessages'],
    }),

    searchGroupUsers: builder.query({
      query: (searchTerm) => ({
        url: `/secure/search-group-users?searchTerm=${encodeURIComponent(searchTerm)}`,
        method: 'GET',
      }),
    }),

    getGroupIdwithUser: builder.query({
      query: ({ username }) => ({
        url: `/chat/returnGroupIdToModerator/${username}`,
        method: 'GET',
      }),
    }),

    getGroupIdwithUserV2: builder.query({
      query: ({ username }) => ({
        url: `/chat/getGroupIdwithUserV2/${username}`,
        method: 'GET',
      }),
    }),

    getGroupIdwithUserV3: builder.query({
      query: ({ username }) => ({
        url: `/chat/getGroupIdwithUserV3/${username}`,
        method: 'GET',
      }),
    }),

    unsendMessage: builder.mutation({
      query: ({ id, chat_moderator_username }) => ({
        url: '/chat/unsendMessage',
        method: 'POST',
        body: { id, chat_moderator_username },
      }),
      invalidatesTags: ['adminGroupMessages'],
    }),
    getAdminChatMessageV2: builder.query({
      query: ({ group_id, chatWithUser, limit = 30, cursor }) => ({
        url: `/chat/getAdminChatMessageV2`,
        method: 'GET',
        params: {
          group_id,
          chatWithUser,
          limit,
          ...(cursor ? { cursor } : {})
        },
      }),
      providesTags: ['adminGroupMessages'],
    }),
    getAdminChatGroupsCounts: builder.query({
      query: () => '/chat/getAdminChatGroupsCounts',
    }),

    getAdminChatGroupsV2: builder.query({
      query: ({ page = 1, limit = 20, tab = 'all', country = '', staff = '' }) => {
        return {
          url: `/chat/getAdminChatGroupsV2?page=${page}&limit=${limit}&tab=${tab}&country=${country}&staff=${staff}`,
          method: "GET",
        };
      },
    }),

    // ==========================================
    // USER-TO-USER CHAT ENDPOINTS
    // ==========================================
    checkOrGetUserConversation: builder.query({
      query: ({ targetUsername }) => ({
        url: '/chat/user-to-user/check',
        method: 'GET',
        params: { targetUsername },
      }),
      invalidatesTags: ['userChat'],
    }),

    getRandomActiveUsers: builder.query({
      query: () => ({
        url: '/secure/active-users/random',
        method: 'GET',
      }),
    }),

    createOrGetUserConversation: builder.mutation({
      query: ({ targetUsername }) => ({
        url: '/chat/user-to-user/create',
        method: 'POST',
        body: { receiverUsername: targetUsername },
      }),
      invalidatesTags: ['userChat'],
    }),

    getUserChatMessages: builder.query({
      query: ({ group_id, limit = 30, cursor }) => ({
        url: '/chat/user-to-user/messages',
        method: 'GET',
        params: {
          group_id,
          limit,
          ...(cursor ? { cursor } : {}),
        },
      }),
      providesTags: ['userChat'],
    }),

    sendUserChatMessage: builder.mutation({
      query: (body) => ({
        url: '/chat/user-to-user/send',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['userChat'],
    }),

    submitUserToUserFeedback: builder.mutation({
      query: ({ group_id, status }) => ({
        url: '/chat/user-to-user/feedback',
        method: 'POST',
        body: { group_id, status },
      }),
      invalidatesTags: ['userChat'],
    }),

    acceptUserChatRequest: builder.mutation({
      query: ({ group_id }) => ({
        url: '/chat/user-to-user/accept',
        method: 'POST',
        body: { group_id },
      }),
      invalidatesTags: ['userChat'],
    }),

    rejectUserChatRequest: builder.mutation({
      query: ({ group_id }) => ({
        url: '/chat/user-to-user/reject',
        method: 'POST',
        body: { group_id },
      }),
      invalidatesTags: ['userChat'],
    }),

    getAllUserChats: builder.query({
      query: ({ page = 1, limit = 20 }) => ({
        url: `/chat/user-to-user/conversations?page=${page}&limit=${limit}`,
        method: 'GET',
      }),
      providesTags: ['userChat'],
    }),

    uploadUserChatImage: builder.mutation({
      query: (formData) => ({
        url: '/chat/user-to-user/upload-image',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['userChat'],
    }),

    deleteUserChatMessage: builder.mutation({
      query: ({ messageId }) => ({
        url: '/chat/unsendMessage',
        method: 'PUT',
        body: { id: messageId },
      }),
      invalidatesTags: ['userChat'],
    }),

    editMessage: builder.mutation({
      query: ({ id, text }) => ({
        url: '/chat/editMessage',
        method: 'PUT',
        body: { id, text },
      }),
      invalidatesTags: ['adminGroupMessages', 'userChat'],
    }),
    blockUserChat: builder.mutation({
      query: (body) => ({
        url: '/chat/user-to-user/block',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['userChat'],
    }),

    unblockUserChat: builder.mutation({
      query: (body) => ({
        url: '/chat/user-to-user/unblock',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['userChat'],
    }),

    toggleUserChatBlock: builder.mutation({
      query: (body) => ({
        url: '/chat/user-to-user/toggle-block',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['userChat'],
    }),
    UpdateGroupConversation: builder.mutation({
      // user register
      query: ({ groupId }) => ({
        url: `/chat/UpdateGroupConversation?groupId=${groupId}`,
        method: 'POST',

      }),
    }),

    // Platform Group Chat
    getPlatformGroupChat: builder.query({
      query: (platformId) => `/chat/platform-group/${platformId}`,
      providesTags: ['Chat'],
    }),
    getChatSupportStaff: builder.query({
      query: () => '/chat/getChatSupportStaff',
    }),
    getPlatformGroupMessages: builder.query({
      query: ({ platformId, limit = 30, cursor }) => ({
        url: `/chat/platform-group/${platformId}/messages`,
        method: 'GET',
        params: { limit, ...(cursor ? { cursor } : {}) },
      }),
      providesTags: ['Chat'],
    }),
    sendPlatformGroupMessage: builder.mutation({
      query: ({ platformId, text }) => ({
        url: `/chat/platform-group/${platformId}/message`,
        method: 'POST',
        body: { text },
      }),
    }),

    getSystemThemes: builder.query({
      query: () => '/chat/themes',
    }),
    updateUserChatTheme: builder.mutation({
      query: (body) => ({
        url: '/chat/user-to-user/update-theme',
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['userChat'],
    }),

    assignGroupToStaff: builder.mutation({
      query: (body) => ({
        url: '/chat/assignGroupToStaff',
        method: 'POST',
        body,
      }),
    }),
    getCategoryTags: builder.query({
      query: () => '/chat/category-tags',
    }),
    updateGroupDetails: builder.mutation({
      query: (body) => ({
        url: '/chat/update-group-details',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['adminChatGroups'],
    }),

    // Automated Customer Care Endpoints
    getAutomatedCategories: builder.query({
      query: () => '/chat/automated-customercare/categories',
      providesTags: ['AutomatedCategories'],
    }),
    createAutomatedCategory: builder.mutation({
      query: (body) => ({
        url: '/chat/automated-customercare/categories',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AutomatedCategories'],
    }),
    updateAutomatedCategory: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/chat/automated-customercare/categories/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['AutomatedCategories'],
    }),
    deleteAutomatedCategory: builder.mutation({
      query: (id) => ({
        url: `/chat/automated-customercare/categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AutomatedCategories'],
    }),
    getAutomatedTags: builder.query({
      query: () => '/chat/automated-customercare/tags',
      providesTags: ['AutomatedTags'],
    }),
    createAutomatedTag: builder.mutation({
      query: (body) => ({
        url: '/chat/automated-customercare/tags',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AutomatedTags'],
    }),
    updateAutomatedTag: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/chat/automated-customercare/tags/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['AutomatedTags'],
    }),
    deleteAutomatedTag: builder.mutation({
      query: (id) => ({
        url: `/chat/automated-customercare/tags/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AutomatedTags'],
    }),
    getAutomatedMessages: builder.query({
      query: (params) => ({
        url: '/chat/automated-customercare/messages',
        method: 'GET',
        params,
      }),
      providesTags: ['AutomatedMessages'],
    }),
    createAutomatedMessage: builder.mutation({
      query: (body) => ({
        url: '/chat/automated-customercare/messages',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AutomatedMessages'],
    }),
    updateAutomatedMessage: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/chat/automated-customercare/messages/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['AutomatedMessages'],
    }),
    deleteAutomatedMessage: builder.mutation({
      query: (id) => ({
        url: `/chat/automated-customercare/messages/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AutomatedMessages'],
    }),
  }),
});

export const {
  // Existing Business Exports
  useGetGroupIdwithAdminQuery,
  useGetGroupIdwithAdminV2Query,
  useGetGroupIdwithAdminV3Query,
  useGetAdminChatMessageV2Query,
  useAddAdminChatImageMutation,
  useAddAdminChatMessageMutation,
  useGetAdminChatGroupsQuery,
  useGetAdminGroupMessagesQuery,
  useGetAdminGroupMessagesV2Query,
  useSearchGroupUsersQuery,
  useGetGroupIdwithUserQuery,
  useGetGroupIdwithUserV2Query,
  useGetGroupIdwithUserV3Query,
  useUnsendMessageMutation,
  useGetAdminChatGroupsV2Query,
  useGetAdminChatGroupsCountsQuery,
  useUpdateGroupConversationMutation,

  // User-to-User Chat hooks
  useGetRandomActiveUsersQuery,
  useCreateOrGetUserConversationMutation,
  useLazyCheckOrGetUserConversationQuery,
  useGetUserChatMessagesQuery,
  useSendUserChatMessageMutation,
  useSubmitUserToUserFeedbackMutation,
  useAcceptUserChatRequestMutation,
  useRejectUserChatRequestMutation,
  useGetAllUserChatsQuery,
  useUploadUserChatImageMutation,
  useDeleteUserChatMessageMutation,
  useEditMessageMutation,
  useBlockUserChatMutation,
  useUnblockUserChatMutation,
  useToggleUserChatBlockMutation,

  // NEW: App-ported Exports
  useGetAllConversationUsersQuery,
  useGetConversationUsersQuery,
  useGetAllMessagesQuery,
  useGetSingleTaskQuery,
  useGetGroupMessagesQuery,
  useAddGroupMessageMutation,
  useGetAvailableGroupsQuery,
  useDeleteMessageByAdminMutation,
  useAddCreatorChatMessageMutation,
  useGetCreatorChatMessageQuery,
  useGetMessageNotificationsQuery,
  useMarkMessageNotificationSeenMutation,
  useDeleteManyMessageNotificationsMutation,
  useDeleteAllMessageNotificationsMutation,

  // Platform Group Chat exports
  useGetPlatformGroupChatQuery,
  useGetPlatformGroupMessagesQuery,
  useSendPlatformGroupMessageMutation,

  // Theme Hooks
  useGetSystemThemesQuery,
  useUpdateUserChatThemeMutation,

  // Staff Assignment Hooks
  useGetChatSupportStaffQuery,
  useAssignGroupToStaffMutation,
  useGetCategoryTagsQuery,
  useUpdateGroupDetailsMutation,

  // Automated Customer Care Hooks
  useGetAutomatedCategoriesQuery,
  useCreateAutomatedCategoryMutation,
  useUpdateAutomatedCategoryMutation,
  useDeleteAutomatedCategoryMutation,
  useGetAutomatedTagsQuery,
  useCreateAutomatedTagMutation,
  useUpdateAutomatedTagMutation,
  useDeleteAutomatedTagMutation,
  useGetAutomatedMessagesQuery,
  useCreateAutomatedMessageMutation,
  useUpdateAutomatedMessageMutation,
  useDeleteAutomatedMessageMutation,
} = chatApi;